import React, { useState, useCallback, useEffect } from "react";
import {
  Grid,
  Typography,
  TextField,
  Autocomplete,
  Button,
  Stack,
} from "@mui/material";
import Dialog from "@mui/material/Dialog";
import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import Slide from "@mui/material/Slide";
import DiagnosisCodeDialog from "components/shared/Dialogs/DiagnosisCodeDialog";
import {
  PXDX,
  DEMOGRAPHICS,
  FREQUENCY,
  INCOMPATIBILITY,
  PRIMARY_REQUIRED,
  PLACE_OF_SERVICE,
  PRIOR_AUTH,
  UNENFORCEABLE,
  tabOrder,
  RULE_OVERRIDES,
  getRuleUnitConfigForTab,
} from "../Constants/PolicyTranslationConstants";
import ProcedureCodeDialog from "components/shared/Dialogs/ProcedureCodeDialog";
import { useDispatch } from "react-redux";
import { setDialog } from "slices/appSlice";
import { formErrorStrfromObj } from "utils/shared";
import api from "api";
import styled from "@emotion/styled";
import Api from "api";

const Transition = React.forwardRef(function Transition(props, ref) {
  // Slide direction is where the dialog comes *from*.
  // "left" => enters from the right side of the screen.
  return <Slide direction="left" ref={ref} {...props} />;
});

const ActionButton = styled(Button)(({ theme }) => ({
  marginLeft: theme.spacing(1),
  textTransform: "uppercase",
}));

const INITIAL_FORM_DATA = {
  ruleName: "",
  ruleDescription: "",
  coverageCriteria: "",
  procedures: [""],
  incompatabilityType: "",
  lookBackPeriod: "",
  referenceCodes: [""],
  dxLimitation: null,
  diagnosis: [""],
  frequencyLimit: { num: "", per: "" },
  POS: "",
  ageRange: { min: "", max: "" },
  gender: "",
  ageGenderReflect: false,
  doHaveDiagnoses: [""],
  dontHaveDiagnoses: [""],
  modifier: [""],
  splitBilling: "",
  bilateralBilling: "",
  repeatBilling: "",
  rationale: "",
};

const INITIAL_ERRORS = {
  ruleName: "",
  ruleDescription: "",
  coverageCriteria: "",
  procedures: "",
  dxLimitation: "",
  diagnoses: "",
  rationale: "",
};

const getPageType = (tabValue) => {
  switch (tabValue) {
    case tabOrder.get(PXDX):
      return "PxDx";
    case tabOrder.get(DEMOGRAPHICS):
      return "Demographics";
    case tabOrder.get(FREQUENCY):
      return "Frequency";
    case tabOrder.get(INCOMPATIBILITY):
      return "Incompatibility";
    case tabOrder.get(PRIMARY_REQUIRED):
      return "Primary/Required";
    case tabOrder.get(PLACE_OF_SERVICE):
      return "Place of Service";
    case tabOrder.get(PRIOR_AUTH):
      return "Prior Auth";
    case tabOrder.get(UNENFORCEABLE):
      return "Unenforceable";
    default:
      return "";
  }
};

const getRuleUnitTypeFromExistingRule = (rule) => {
  if (!rule) {
    return "";
  }

  if (typeof rule.ruleUnitType === "string") {
    return rule.ruleUnitType;
  }

  return rule.ruleUnitType?.ruleType || rule.ruleUnitType?.sheetName || "";
};

const getRuleUnitConfigForDialog = (tabValue, selectedRule) => {
  const configured = getRuleUnitConfigForTab(tabValue);

  if (configured) {
    return configured;
  }

  return {
    ruleUnitType: getRuleUnitTypeFromExistingRule(selectedRule),
    ruleUnitTabName:
      getPageType(tabValue) || selectedRule?.ruleUnitTabName || "",
  };
};

const normalizeDxLimitation = (dxLimitation) => {
  if (!dxLimitation) {
    return null;
  }

  if (Array.isArray(dxLimitation)) {
    if (dxLimitation.length === 0) {
      return null;
    }

    const firstValue = dxLimitation[0];
    if (typeof firstValue === "object" && firstValue !== null) {
      return firstValue;
    }

    return { dxLimitationKey: firstValue };
  }

  if (typeof dxLimitation === "object") {
    return dxLimitation;
  }

  return { dxLimitationKey: dxLimitation };
};

export default function RuleDetailsDialog({
  open,
  onClose,
  title,
  children,
  healthPlanKey,
  policyState,
  tabValue,
  setLoading,
  onAddRule,
  onCreateVariance,
  selectedRule,
  rules,
  readOnly = false,
  canCreateVariance = true,
}) {
  const [formData, setFormData] = useState(INITIAL_FORM_DATA);
  const [codeGroup, setCodeGroup] = useState([]);
  const [selectedGroupsProcedure, setSelectedGroupsProcedure] = useState([]);
  const [selectedGroupsDiagnoses, setSelectedGroupsDiagnoses] = useState([]);
  const [isPxDialogOpen, setIsPxDialogOpen] = useState(false);
  const [openDxDialog, setOpenDxDialog] = useState(false);
  const [dxLimitationValues, setDxLimitationValues] = useState([]);
  const [errors, setErrors] = useState(INITIAL_ERRORS);
  const dispatch = useDispatch();
  const pageType = getPageType(tabValue);

  const resetDialogState = useCallback(() => {
    setFormData(INITIAL_FORM_DATA);
    setSelectedGroupsProcedure([]);
    setSelectedGroupsDiagnoses([]);
    setErrors(INITIAL_ERRORS);
  }, []);

  const populateDialogFromSelectedRule = useCallback(() => {
    if (!selectedRule) {
      resetDialogState();
      return;
    }

    const selectedProcedureGroups = selectedRule.procedureCodeGroups || [];
    const selectedDiagnosisGroups = selectedRule.diagnosisCodeGroups || [];

    setFormData((prev) => ({
      ...prev,
      ruleName: selectedRule.ruleUnitName || selectedRule.ruleName || "",
      ruleDescription: selectedRule.ruleDescription || "",
      coverageCriteria:
        selectedRule.coverageCriteriaList ||
        selectedRule.coverageCriteria ||
        "",
      dxLimitation: normalizeDxLimitation(selectedRule.dxLimitation),
      rationale: selectedRule.ruleRationale || selectedRule.rationaleText || "",
    }));
    setSelectedGroupsProcedure(selectedProcedureGroups);
    setSelectedGroupsDiagnoses(selectedDiagnosisGroups);
    setErrors(INITIAL_ERRORS);
  }, [resetDialogState, selectedRule]);

  const handleDialogOpen = useCallback(() => {
    populateDialogFromSelectedRule();
  }, [populateDialogFromSelectedRule]);

  const handleDialogClose = useCallback(
    (...args) => {
      resetDialogState();
      onClose(...args);
    },
    [onClose, resetDialogState]
  );
  const addRowsToGrid = (pxCodeList) => {
    // Prepare new codes, only add if not already present
    const currentProcedures = Array.isArray(formData.procedures)
      ? formData.procedures
      : formData.procedures
        ? formData.procedures.split(",")
        : [];
    const newCodes = pxCodeList.map((item) => item.code);
    const mergedProcedures = Array.from(
      new Set([...currentProcedures, ...newCodes])
    );
    setFormData({
      ...formData,
      procedures: mergedProcedures,
    });
  };

  const handleAddPxCodesToGrid = (pxCodes) => {
    addRowsToGrid(pxCodes);
    setIsPxDialogOpen(false);
  };

  const normalizeCoverageCriteria = (raw) =>
    raw
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean)
      .join(", ");

  const formatCodeGroupLabel = (group) =>
    `${group.codeGroupName} (ver ${group.codeGroupVersion})`;

  const validatePxDxForm = () => {
    const newErrors = { ...INITIAL_ERRORS };
    let valid = true;

    if (!formData.ruleName.trim()) {
      newErrors.ruleName = "Rule Name is required.";
      valid = false;
    } else if (formData.ruleName.length > 250) {
      newErrors.ruleName = "Rule Name must be 250 characters or fewer.";
      valid = false;
    }

    if (!formData.ruleDescription.trim()) {
      newErrors.ruleDescription = "Rule Description is required.";
      valid = false;
    } else if (formData.ruleDescription.length > 1500) {
      newErrors.ruleDescription =
        "Rule Description must be 1500 characters or fewer.";
      valid = false;
    }

    const coverageCriteriaInput = formData.coverageCriteria.trim();
    if (!coverageCriteriaInput) {
      newErrors.coverageCriteria = "Coverage Criteria is required.";
      valid = false;
    } else {
      const rawSegments = coverageCriteriaInput.split(",").map((s) => s.trim());
      const hasEmptySegment = rawSegments.some((segment) => !segment);
      const hasWhitespaceSeparatedValues = rawSegments.some((segment) =>
        /\s+/.test(segment)
      );
      const hasCommaSeparatedValues =
        rawSegments.length >= 2 &&
        !hasEmptySegment &&
        !hasWhitespaceSeparatedValues;
      const hasSingleValue =
        rawSegments.length === 1 &&
        !!rawSegments[0] &&
        !hasWhitespaceSeparatedValues;
      const isValidCoverageCriteria = hasSingleValue || hasCommaSeparatedValues;

      if (tabValue === tabOrder.get(PXDX) && !isValidCoverageCriteria) {
        newErrors.coverageCriteria =
          "Coverage Criteria must be comma-separated (example: value, value).";
        valid = false;
      }
    }

    if (selectedGroupsProcedure.length === 0) {
      newErrors.procedures = "Procedures is required.";
      valid = false;
    }

    if (!formData.dxLimitation?.dxLimitationKey) {
      newErrors.dxLimitation = "DX Limitation is required.";
      valid = false;
    }

    if (selectedGroupsDiagnoses.length === 0) {
      newErrors.diagnoses = "Diagnoses is required.";
      valid = false;
    }

    if (formData.rationale.length > 1500) {
      newErrors.rationale = "Rationale must be 1500 characters or fewer.";
      valid = false;
    }

    setErrors(newErrors);
    return valid;
  };

  const handleAddToPolicy = () => {
    if (!validatePxDxForm()) return;

    const ruleUnitConfig = getRuleUnitConfigForDialog(tabValue, selectedRule);

    const normalizedCoverageCriteria = normalizeCoverageCriteria(
      formData.coverageCriteria
    );
    const procedureLabels = selectedGroupsProcedure.map(formatCodeGroupLabel);
    const diagnosisLabels = selectedGroupsDiagnoses.map(formatCodeGroupLabel);
    const dxLimitationLabel = formData.dxLimitation?.displayValueText || "";

    const rule = {
      ruleUnitKey: selectedRule?.ruleUnitKey || null,
      ruleUnitId: selectedRule?.ruleUnitId || null,
      policyKey: policyState?.policyKey || null,
      isActive: selectedRule?.isActive ?? true,
      isVariance: selectedRule?.isVariance ?? false,
      ruleUnitVersion: selectedRule?.ruleUnitVersion || null,
      ruleUnitType: ruleUnitConfig.ruleUnitType,
      ruleUnitTabName: ruleUnitConfig.ruleUnitTabName,
      ruleUnitName: formData.ruleName.trim(),
      ruleDescription: formData.ruleDescription.trim(),
      ruleRationale: formData.rationale.trim(),
      changeControl: normalizedCoverageCriteria,
      coverageCriteriaList: normalizedCoverageCriteria,
      procedures: procedureLabels.join(", "),
      ruleType: dxLimitationLabel || "DXLimitation",
      diagnoses: diagnosisLabels.length > 0,
      modifiers: false,
      rationale: Boolean(formData.rationale.trim()),
      adoptionDetails: [],
      procedureCodeGroups: selectedGroupsProcedure,
      diagnosisCodeGroups: selectedGroupsDiagnoses,
      dxLimitation: formData.dxLimitation || null,
      rationaleText: formData.rationale.trim(),
      editRuleUnit: selectedRule?.ruleUnitKey
        ? (selectedRule.editRuleUnit || "EDIT").toUpperCase() === "NEW"
          ? "NEW"
          : "EDIT"
        : "NEW",
      ruleName: formData.ruleName.trim(),
      cc: normalizedCoverageCriteria,
      version:
        selectedRule?.ruleUnitVersion !== null &&
        selectedRule?.ruleUnitVersion !== undefined
          ? String(selectedRule.ruleUnitVersion)
          : "",
    };
    if (onAddRule) onAddRule(rule);
    setFormData(INITIAL_FORM_DATA);
    setSelectedGroupsProcedure([]);
    setSelectedGroupsDiagnoses([]);
    setErrors(INITIAL_ERRORS);
    handleDialogClose();
  };

  const showCreateVariance =
    selectedRule && healthPlanKey !== 999 && !selectedRule.isVariance;
  const handleCreateVariance = () => {
    const ruleUnitConfig = getRuleUnitConfigForDialog(tabValue, selectedRule);
    const normalizedCoverageCriteria = normalizeCoverageCriteria(
      formData.coverageCriteria
    );
    const procedureLabels = selectedGroupsProcedure.map(formatCodeGroupLabel);
    const diagnosisLabels = selectedGroupsDiagnoses.map(formatCodeGroupLabel);
    const dxLimitationLabel = formData.dxLimitation?.displayValueText || "";

    const varianceRule = {
      ruleUnitType: ruleUnitConfig.ruleUnitType,
      ruleUnitTabName: ruleUnitConfig.ruleUnitTabName,
      ruleUnitName: formData.ruleName.trim(),
      ruleDescription: formData.ruleDescription.trim(),
      ruleRationale: formData.rationale.trim(),
      changeControl: normalizedCoverageCriteria,
      coverageCriteriaList: normalizedCoverageCriteria,
      procedures: procedureLabels.join(", "),
      ruleType: dxLimitationLabel || "DXLimitation",
      diagnoses: diagnosisLabels.length > 0,
      modifiers: false,
      adoptionDetails: [],
      procedureCodeGroups: selectedGroupsProcedure,
      diagnosisCodeGroups: selectedGroupsDiagnoses,
      dxLimitation: formData.dxLimitation || null,
      rationaleText: formData.rationale.trim(),
      rationale: Boolean(formData.rationale.trim()),
      ruleName: formData.ruleName.trim(),
      cc: normalizedCoverageCriteria,
      policyKey: policyState?.policyKey || null,
      isActive: selectedRule?.isActive ?? true,
      // Variance-specific overrides
      ruleUnitKey: null,
      ruleUnitId: null,
      isVariance: true,
      editRuleUnit: "NEW",
      ruleUnitParent: selectedRule?.ruleUnitKey || null,
      ruleUnitVersion: 1,
      version: "1",
    };

    if (onCreateVariance) onCreateVariance(varianceRule);
  };

  const handleCreateNewVersion = () => {
    if (!selectedRule) return;

    // Create new rule unit version, excluding adoption configuration details
    const newRuleUnit = {
      ...selectedRule,
      ruleUnitKey: null,
      ruleUnitVersion: (selectedRule.ruleUnitVersion || 0) + 1,
      editRuleUnit: "NEW",
      adoptionConfigurations: [],
      ruleUnitParentKey: selectedRule.ruleUnitKey,
      ruleUnitId: selectedRule.ruleUnitId,
    };

    // Dispatch action to create the new rule unit
    if (onAddRule) onAddRule(newRuleUnit);

    // Refresh the dialog with the new rule unit details
    setFormData(newRuleUnit);
  };

  /**
   * Determine if "Create New Version" action should be displayed based on:
   * - For AHCS (healthPlanKey === 999: display if rule is compiled
   * - For non-AHCS: display if rule is variance AND compiled
   */
  const showCreateNewVersion =
    selectedRule?.immutable === true &&
    (healthPlanKey === 999 ||
      (healthPlanKey !== 999 && selectedRule.isVariance));

  const onDxDialogClose = () => {
    setOpenDxDialog(false);
  };
  // Fetch collapsed range for given diagnosis codes
  const fetchCollapsedRange = async (codes) => {
    if (!codes.length) {
      return [];
    }
    dispatch(setLoading(true));
    try {
      const response = await api.policyCoding.getDxCollapsed(codes);
      const list = response.data;
      return list.length ? list : [];
    } catch (error) {
      const message =
        formErrorStrfromObj(error) || "An error occurred during the search.";
      dispatch(setDialog({ message, title: "Error" }));
    } finally {
      dispatch(setLoading(false));
    }
  };

  const handleSaveDx = (dxCodes) => {
    // Convert current diagnosis to array if it's a string
    const currentDx = Array.isArray(formData.diagnosis)
      ? formData.diagnosis
      : formData.diagnosis
        ? formData.diagnosis.split(",")
        : [];
    // Add new codes, avoiding duplicates
    const mergedDx = Array.from(new Set([...currentDx, ...dxCodes]));
    setFormData({ ...formData, diagnosis: mergedDx });
    setOpenDxDialog(false);
  };

  useEffect(() => {
    const fetchDxLimitationValues = async () => {
      try {
        setLoading(true);
        const response = await api.PolicyRules.getDxLimitationList();
        if (
          response.status === 200 &&
          response.data !== null &&
          response.data.length > 0
        ) {
          setDxLimitationValues(response.data);
        } else if (response.data == null || response.data.length === 0) {
          throw new Error("No Dx Limitation Values found.");
        }
      } catch (e) {
        const message =
          formErrorStrfromObj(e) || "Unable to load Dx Limitation values.";
        dispatch(setDialog({ message, title: "Error" }));
      } finally {
        setLoading(false);
      }
    };

    fetchDxLimitationValues();
  }, [dispatch]);

  return (
    <Dialog
      open={open}
      onClose={handleDialogClose}
      fullWidth={true}
      maxWidth="lg"
      slots={{ transition: Transition }}
      TransitionProps={{
        onEnter: handleDialogOpen,
        onExited: resetDialogState,
      }}
    >
      <AppBar sx={{ position: "relative" }}>
        <Toolbar>
          <IconButton
            edge="start"
            color="inherit"
            onClick={handleDialogClose}
            aria-label="close"
          >
            <CloseIcon />
          </IconButton>
          <Typography sx={{ ml: 2, flex: 1 }} variant="h6" component="div">
            {title || `Rule Details: ${pageType}`}
          </Typography>
        </Toolbar>
      </AppBar>
      <div style={{ padding: 24 }}>
        {/* Rule Name field always visible */}
        <Grid
          container
          alignItems="center"
          spacing={2}
          sx={{ marginBottom: 2 }}
        >
          <Grid item xs={3}>
            <Typography
              variant="body1"
              sx={{ fontWeight: "bold", textAlign: "left", minWidth: 400 }}
            >
              Rule Name:
            </Typography>
          </Grid>
          <Grid item xs={9} sx={{ flexGrow: 1 }}>
            <TextField
              id="ruleName"
              variant="outlined"
              value={formData.ruleName}
              onChange={(e) =>
                setFormData({ ...formData, ruleName: e.target.value })
              }
              fullWidth
              disabled={readOnly}
              error={!!errors.ruleName}
              helperText={errors.ruleName}
            />
          </Grid>
        </Grid>
        {/* Rule Description field always visible */}
        <Grid
          container
          alignItems="flex-start"
          spacing={2}
          sx={{ marginBottom: 2 }}
        >
          <Grid item xs={3}>
            <Typography
              variant="body1"
              sx={{ fontWeight: "bold", textAlign: "left", minWidth: 400 }}
            >
              Rule Description:
            </Typography>
            <Typography
              variant="body2"
              color="textSecondary"
              component="div"
              sx={{
                fontWeight: "bold",
                textAlign: "left",
                minWidth: 400,
                marginTop: 0,
              }}
            >
              If applicable, be sure to include:
              <ul style={{ marginTop: 0 }}>
                <li>Populations for which the rule is intended</li>
                <li>Populations for which the rule is too restrictive</li>
              </ul>
            </Typography>
          </Grid>
          <Grid item xs={9} sx={{ flexGrow: 1 }}>
            <TextField
              id="ruleDescription"
              variant="outlined"
              value={formData.ruleDescription}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  ruleDescription: e.target.value,
                })
              }
              multiline
              minRows={3}
              fullWidth
              disabled={readOnly}
              error={!!errors.ruleDescription}
              helperText={errors.ruleDescription}
            />
          </Grid>
        </Grid>
        {/* Coverage Criteria field always visible */}
        <Grid
          container
          alignItems="center"
          spacing={2}
          sx={{ marginBottom: 2 }}
        >
          <Grid item xs={3}>
            <Typography
              variant="body1"
              sx={{ fontWeight: "bold", textAlign: "left", minWidth: 400 }}
            >
              Coverage Criteria:
            </Typography>
            <Typography
              variant="body1"
              sx={{ textAlign: "left", minWidth: 400 }}
            >
              comma separate
            </Typography>
          </Grid>
          <Grid item xs={9} sx={{ flexGrow: 1 }}>
            <TextField
              sx={{ width: "100%" }}
              id="coverageCriteria"
              variant="outlined"
              value={formData.coverageCriteria}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  coverageCriteria: e.target.value,
                })
              }
              disabled={readOnly}
              error={!!errors.coverageCriteria}
              helperText={errors.coverageCriteria}
            />
          </Grid>
        </Grid>
        {/* All other form fields only render if tabValue matches PXDX */}
        {tabValue === tabOrder.get(PXDX) && (
          <React.Fragment>
            <Grid
              container
              alignItems="center"
              spacing={2}
              sx={{ marginBottom: 2 }}
            >
              <Grid item xs={3}>
                <Typography
                  variant="body1"
                  sx={{ fontWeight: "bold", textAlign: "left", minWidth: 400 }}
                >
                  Procedures:
                </Typography>
              </Grid>
              <Grid item xs={9} sx={{ flexGrow: 1 }}>
                <Autocomplete
                  sx={{ pt: 1 }}
                  fullWidth
                  multiple
                  data-testid="procedures-autocomplete"
                  getOptionKey={(option) => option.codeGroupKey}
                  options={
                    policyState?.codeGroups?.filter(
                      (group) => group.codeGroupType === "P"
                    ) || []
                  }
                  getOptionLabel={(option) => formatCodeGroupLabel(option)}
                  value={selectedGroupsProcedure}
                  onChange={(event, newValue) =>
                    setSelectedGroupsProcedure(newValue)
                  }
                  disabled={readOnly}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Select an option"
                      placeholder="Choose an option"
                      error={!!errors.procedures}
                      helperText={errors.procedures}
                    />
                  )}
                />
              </Grid>
            </Grid>
            {/* Dx Limitation field moved below Procedures */}
            <Grid
              container
              alignItems="center"
              spacing={2}
              sx={{ marginBottom: 2 }}
            >
              <Grid item xs={3}>
                <Typography
                  variant="body1"
                  sx={{ fontWeight: "bold", textAlign: "left", minWidth: 400 }}
                >
                  Dx Limitation:
                </Typography>
              </Grid>
              <Grid item xs={9} sx={{ flexGrow: 1 }}>
                <Autocomplete
                  options={dxLimitationValues.filter(
                    (opt) => opt.tabDisplayText === "PxDx"
                  )}
                  getOptionLabel={(option) =>
                    option?.displayValueText || String(option)
                  }
                  value={formData.dxLimitation}
                  onChange={(event, newValue) =>
                    setFormData({
                      ...formData,
                      dxLimitation: newValue || null,
                    })
                  }
                  isOptionEqualToValue={(option, value) =>
                    option?.dxLimitationKey === value?.dxLimitationKey
                  }
                  disabled={readOnly}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Dx Limitation"
                      variant="outlined"
                      error={!!errors.dxLimitation}
                      helperText={errors.dxLimitation}
                    />
                  )}
                />
              </Grid>
            </Grid>
            {/* Diagnoses field moved below Dx Limitation */}
            <Grid
              container
              alignItems="center"
              spacing={2}
              sx={{ marginBottom: 2 }}
            >
              <Grid item xs={3}>
                <Typography
                  variant="body1"
                  sx={{ fontWeight: "bold", textAlign: "left", minWidth: 400 }}
                >
                  Diagnoses:
                </Typography>
              </Grid>
              <Grid item xs={9} sx={{ flexGrow: 1 }}>
                <Autocomplete
                  sx={{ pt: 1 }}
                  fullWidth
                  multiple
                  data-testid="diagnoses-autocomplete"
                  getOptionKey={(option) => option.codeGroupKey}
                  options={
                    policyState?.codeGroups?.filter(
                      (group) => group.codeGroupType === "D"
                    ) || []
                  }
                  getOptionLabel={(option) => formatCodeGroupLabel(option)}
                  value={selectedGroupsDiagnoses}
                  onChange={(event, newValue) =>
                    setSelectedGroupsDiagnoses(newValue)
                  }
                  disabled={readOnly}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Select an option"
                      placeholder="Choose an option"
                      error={!!errors.diagnoses}
                      helperText={errors.diagnoses}
                    />
                  )}
                />
              </Grid>
            </Grid>
            {/* Rationale multi-line text field below Diagnoses */}
            <Grid
              container
              alignItems="flex-start"
              spacing={2}
              sx={{ marginBottom: 2 }}
            >
              <Grid item xs={3}>
                <Typography
                  variant="body1"
                  sx={{ fontWeight: "bold", textAlign: "left", minWidth: 400 }}
                >
                  Rationale:
                </Typography>
              </Grid>
              <Grid item xs={9} sx={{ flexGrow: 1 }}>
                <TextField
                  id="rationale"
                  variant="outlined"
                  value={formData.rationale}
                  onChange={(e) =>
                    setFormData({ ...formData, rationale: e.target.value })
                  }
                  multiline
                  minRows={3}
                  fullWidth
                  disabled={readOnly}
                  error={!!errors.rationale}
                  helperText={errors.rationale}
                />
              </Grid>
            </Grid>
            <Grid
              container
              sx={{
                marginBottom: 2,
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <div style={{ display: "flex", gap: 8 }}>
                {showCreateVariance &&
                  canCreateVariance &&
                  policyState?.editMode === true && (
                    <ActionButton
                      variant="contained"
                      color="primary"
                      onClick={handleCreateVariance}
                    >
                      Create Variance
                    </ActionButton>
                  )}
                {/* && !readOnly */}
                {showCreateNewVersion &&
                  readOnly &&
                  policyState?.editMode === true && (
                    <ActionButton
                      variant="contained"
                      color="primary"
                      onClick={handleCreateNewVersion}
                    >
                      Create New Version
                    </ActionButton>
                  )}
              </div>
              <div style={{ display: "flex", gap: 8 }}>
                <ActionButton
                  variant="contained"
                  color="button2025RebrandSecondary"
                  sx={{ minWidth: 120 }}
                  onClick={onClose}
                >
                  Cancel
                </ActionButton>
                {!readOnly && (
                  <ActionButton
                    variant="contained"
                    color="button2025RebrandPrimary"
                    sx={{ minWidth: 120 }}
                    onClick={handleAddToPolicy}
                  >
                    Add to Policy
                  </ActionButton>
                )}
              </div>
            </Grid>
          </React.Fragment>
        )}
      </div>
      {isPxDialogOpen && (
        <ProcedureCodeDialog
          open={isPxDialogOpen}
          onClose={() => setIsPxDialogOpen(false)}
          onSave={handleAddPxCodesToGrid}
        />
      )}
      {openDxDialog && (
        <DiagnosisCodeDialog
          open={openDxDialog}
          dxCodes={formData.diagnosis}
          onClose={() => onDxDialogClose()}
          onSave={handleSaveDx}
          readOnly={false}
        />
      )}
    </Dialog>
  );
}
